import { useEffect, useState } from 'react';
import { apiErrorText, mailApi, type InboundEmailDetail } from '@/shared/api';
import { Callout, Dialog, DialogContent, DialogTitle, ErrorState, Scroller, Skeleton } from '@/shared/ui';
import s from './mail.module.css';

const AUTH_LABEL: Record<string, string> = {
  pass: 'пройдена',
  fail: 'не пройдена — возможна подделка адреса',
  none: 'сервер не сообщил',
};

interface Props {
  ticketId: string;
  messageId: string | null;
  onClose: () => void;
}

export function OriginalEmailDialog({ ticketId, messageId, onClose }: Props) {
  const [data, setData] = useState<InboundEmailDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!messageId) return;
    setData(null);
    setError(null);
    mailApi
      .originalEmail(ticketId, messageId)
      .then((res) => setData(res.data))
      .catch((err) => setError(apiErrorText(err, 'Не удалось загрузить письмо')));
  }, [ticketId, messageId]);

  const rejected = data?.attachments.filter((a) => a.status === 'rejected') ?? [];

  return (
    <Dialog open={messageId != null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={s.originalDialog}>
        <DialogTitle>Оригинал письма</DialogTitle>
        {error ? (
          <ErrorState message={error} />
        ) : !data ? (
          <div className={s.originalSkeleton}>
            <Skeleton height={14} width="60%" />
            <Skeleton height={14} width="40%" />
            <Skeleton height={160} />
          </div>
        ) : (
          <>
            <dl className={s.headers}>
              <dt>От</dt>
              <dd>{data.fromName ? `${data.fromName} <${data.from}>` : data.from}</dd>
              <dt>Кому</dt>
              <dd>{data.to.join(', ') || '—'}</dd>
              {data.cc.length > 0 && (
                <>
                  <dt>Копия</dt>
                  <dd>{data.cc.join(', ')}</dd>
                </>
              )}
              <dt>Тема</dt>
              <dd>{data.subject || '(без темы)'}</dd>
              <dt>Отправлено</dt>
              <dd>{data.sentAt ? new Date(data.sentAt).toLocaleString('ru') : '—'}</dd>
              <dt>Проверка отправителя</dt>
              <dd>{AUTH_LABEL[data.authResult] ?? data.authResult}</dd>
            </dl>
            {rejected.length > 0 && (
              <Callout tone="warning" title="Не все файлы попали в заявку">
                {rejected.map((a) => `${a.fileName}${a.reason ? ` — ${a.reason}` : ''}`).join('; ')}
              </Callout>
            )}
            <Scroller className={s.originalBody}>
              <pre className={s.originalText}>{data.textBody || '(письмо без текста)'}</pre>
            </Scroller>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
